import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchJson } from '@/lib/api';

/**
 * Codigos de aprobacion de la oficina. El backend los emitia desde siempre,
 * pero ninguna pantalla lo permitia, asi que "llame a la oficina" no tenia
 * salida. El tablero muestra esta pestana solo si la lista responde: el
 * servidor da 403 al resto.
 */
export default function PanelCodigos({ codes, token, onChange }) {
  const [max, setMax] = useState('');
  const [motivo, setMotivo] = useState('');
  const [comerciante, setComerciante] = useState('');
  const [dias, setDias] = useState('7');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nuevo, setNuevo] = useState(null);

  const emitir = async () => {
    setBusy(true); setError(''); setNuevo(null);
    try {
      const d = await fetchJson('/insurance/selletrans/override/create', {
        token, method: 'POST',
        body: {
          max_value: parseFloat(max),
          reason: motivo.trim(),
          valid_days: parseInt(dias, 10) || 7,
          single_use: true,
          // Por encima de 50.000 el codigo va a nombre de un comerciante con el
          // techo aprobado en su setup (Mike, 15-oct). Por debajo es opcional.
          merchant_email: comerciante.trim() || null,
          // Un codigo por mercaderia no tiene por que saltar el tope de los
          // ZIP de alto riesgo. El backend lo daba por defecto.
          zip_bypass: false,
        },
      });
      setNuevo(d); setMax(''); setMotivo(''); setComerciante('');
      await onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revocar = async (code) => {
    try {
      await fetchJson(`/insurance/selletrans/override/revoke/${code}`, { token, method: 'POST' });
      await onChange();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="codes-panel">
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <h3 className="font-semibold mb-1">Issue an approval code</h3>
        <p className="text-xs text-slate-500 mb-3">
          For a merchant who called the office: case-by-case goods, fragile items, or a value above the
          limit. The code works once, for one shipment, up to the amount you set, and the reason stays on record.
          It cannot lift the limits of the coverage program itself: until Lloyd's goes live, coverage stops at
          $25,000 per package for every merchant.
        </p>
        <div className="grid sm:grid-cols-[1fr_2fr_2fr_auto_auto] gap-2 items-end">
          <label className="text-xs text-slate-500">Up to (USD)
            <input type="number" min="1" className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" value={max} onChange={e => setMax(e.target.value)} data-testid="c-max" />
          </label>
          <label className="text-xs text-slate-500">Merchant email (required above $25,000)
            <input type="email" className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" value={comerciante} onChange={e => setComerciante(e.target.value)} data-testid="c-merchant" />
          </label>
          <label className="text-xs text-slate-500">Reason (merchant, item, who approved)
            <input className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" value={motivo} onChange={e => setMotivo(e.target.value)} data-testid="c-reason" />
          </label>
          <label className="text-xs text-slate-500">Valid days
            <input type="number" min="1" max="30" className="w-20 h-10 px-3 mt-1 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" value={dias} onChange={e => setDias(e.target.value)} data-testid="c-days" />
          </label>
          <Button onClick={emitir} disabled={busy || !max || !motivo.trim()} data-testid="c-issue">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Issue code'}
          </Button>
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        {nuevo && (
          <p className="mt-3 text-sm" data-testid="c-new">
            Read this code to the merchant: <span className="font-mono text-lg font-bold tracking-wider">{nuevo.code}</span>
            <span className="text-slate-500"> · up to ${Number(nuevo.max_value).toLocaleString()}{nuevo.merchant_email ? ` · only for ${nuevo.merchant_email}` : ''} · expires {new Date(nuevo.expires_at).toLocaleDateString()}</span>
          </p>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Up to</th>
              <th className="px-4 py-3 text-left">Merchant</th>
              <th className="px-4 py-3 text-left">Reason</th>
              <th className="px-4 py-3 text-left">Issued</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="codes-rows">
            {codes.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No codes issued yet.</td></tr>}
            {codes.map(c => (
              <tr key={c.code} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
                <td className="px-4 py-3 tabular-nums">${Number(c.max_value).toLocaleString()}</td>
                <td className="px-4 py-3 text-xs">{c.merchant_email || 'Any'}</td>
                <td className="px-4 py-3 text-xs">{c.reason}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-xs">
                  {c.used_verification_number ? `Used on ${c.used_verification_number}`
                    : c.revoked_at ? 'Revoked'
                    : !c.active ? 'Inactive'
                    : new Date(c.expires_at) < new Date() ? 'Expired'
                    : `Active until ${new Date(c.expires_at).toLocaleDateString()}`}
                </td>
                <td className="px-4 py-3 text-right">
                  {c.active && !c.used_verification_number && (
                    <button className="text-xs text-red-600 hover:underline" onClick={() => revocar(c.code)}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
